import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import InputAdornment from "@mui/material/InputAdornment";
import List from "@mui/material/List";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemText from "@mui/material/ListItemText";
import ListSubheader from "@mui/material/ListSubheader";
import CircularProgress from "@mui/material/CircularProgress";
import Alert from "@mui/material/Alert";
import Chip from "@mui/material/Chip";
import SearchIcon from "@mui/icons-material/Search";
import dayjs from "dayjs";
import useHelp from "./hooks/useHelp";
import MarkdownView from "./components/MarkdownView";

/**
 * Help: the contents on the left, grouped by the phase of the year, search
 * above them, and the open page on the right. Pages come live from the API,
 * which has already decided what this account may see on this instance.
 */
export default function HelpPage({ initialSlug }) {
  const help = useHelp(initialSlug);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: { xs: "column", md: "row" },
        gap: 2,
        p: { xs: 1, sm: 2 },
        maxWidth: 1400,
        mx: "auto",
        alignItems: "flex-start",
        // .App centres all text (App.css); reading pages are left-aligned.
        textAlign: "left",
      }}
    >
      <Paper
        variant="outlined"
        sx={{
          width: { xs: "100%", md: 300 },
          flexShrink: 0,
          position: { md: "sticky" },
          top: { md: 16 },
          maxHeight: { md: "calc(100vh - 110px)" },
          overflowY: "auto",
        }}
      >
        <Box sx={{ p: 1.5, pb: 0.5 }}>
          <Typography variant="h6" sx={{ mb: 1 }}>
            Help
          </Typography>
          <TextField
            size="small"
            fullWidth
            placeholder="Search help"
            value={help.query}
            onChange={(e) => help.setQuery(e.target.value)}
            inputProps={{ "aria-label": "Search help" }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
              endAdornment: help.searching ? <CircularProgress size={16} /> : null,
            }}
          />
        </Box>

        {help.results ? (
          <SearchResults results={help.results} onOpen={help.open} current={help.slug} />
        ) : (
          <Contents help={help} />
        )}
      </Paper>

      <Paper variant="outlined" sx={{ flex: 1, minWidth: 0, width: "100%", p: { xs: 2, sm: 3 } }}>
        <PageBody help={help} />
      </Paper>
    </Box>
  );
}

function Contents({ help }) {
  if (help.contentsState === "loading") {
    return (
      <Box sx={{ p: 2, display: "flex", justifyContent: "center" }}>
        <CircularProgress size={24} />
      </Box>
    );
  }
  if (help.contentsState === "error") {
    return <Alert severity="error" sx={{ m: 1.5 }}>Help could not be loaded. Try again in a moment.</Alert>;
  }
  if (help.sections.length === 0) {
    return (
      <Typography variant="body2" sx={{ p: 2, opacity: 0.7 }}>
        No help pages have been published here yet.
      </Typography>
    );
  }
  return (
    <List dense disablePadding aria-label="Help contents">
      {help.sections.map((g) => (
        <li key={g.section}>
          <ul style={{ padding: 0 }}>
            <ListSubheader sx={{ lineHeight: "32px", fontWeight: 700 }}>{g.label}</ListSubheader>
            {g.pages.map((p) => (
              <ListItemButton key={p.slug} selected={p.slug === help.slug} onClick={() => help.open(p.slug)} sx={{ pl: 3 }}>
                <ListItemText primary={p.title} primaryTypographyProps={{ variant: "body2" }} />
              </ListItemButton>
            ))}
          </ul>
        </li>
      ))}
    </List>
  );
}

function SearchResults({ results, onOpen, current }) {
  if (results.length === 0) {
    return (
      <Typography variant="body2" sx={{ p: 2, opacity: 0.7 }}>
        Nothing found.
      </Typography>
    );
  }
  return (
    <List dense aria-label="Search results">
      {results.map((r) => (
        <ListItemButton key={r.slug} selected={r.slug === current} onClick={() => onOpen(r.slug)} alignItems="flex-start">
          <ListItemText
            primary={r.title}
            secondary={r.snippet}
            primaryTypographyProps={{ variant: "body2", fontWeight: 600 }}
            secondaryTypographyProps={{ variant: "caption" }}
          />
        </ListItemButton>
      ))}
    </List>
  );
}

function PageBody({ help }) {
  if (help.pageState === "loading" || (help.pageState === "idle" && help.contentsState === "loading")) {
    return (
      <Box sx={{ p: 4, display: "flex", justifyContent: "center" }}>
        <CircularProgress />
      </Box>
    );
  }
  if (help.pageState === "missing") {
    return <Alert severity="info">That help page isn't available here.</Alert>;
  }
  if (help.pageState === "error") {
    return <Alert severity="error">The page could not be loaded. Try again in a moment.</Alert>;
  }
  if (!help.page) {
    return (
      <Typography variant="body2" sx={{ opacity: 0.7 }}>
        Choose a page from the contents.
      </Typography>
    );
  }
  const { page } = help;
  return (
    <article>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap", mb: 1 }}>
        <Typography variant="h4" component="h1" sx={{ fontSize: { xs: "1.6rem", sm: "2rem" } }}>
          {page.title}
        </Typography>
        {page.audience === "admin" && <Chip size="small" label="Admin" color="warning" variant="outlined" />}
      </Box>
      {page.updatedAt && (
        <Typography variant="caption" sx={{ display: "block", mb: 2, opacity: 0.6 }}>
          Updated {dayjs(page.updatedAt).format("D MMM YYYY")}
        </Typography>
      )}
      <MarkdownView text={page.body} />
    </article>
  );
}
